import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableContainer } from "@mui/material";
import { QuickChargerRemainingTime } from "@/models/quickChargerRemainingTime";
import { QuickChargerRemainingTimeConstants } from "./QuickChargerRemainingTimeConstants";
import { ChargerDivision } from "@/models/chargeSupplyTransaction";
import { formatKwh } from "@/util";
import { getShortDatetime } from "@/util/datetime";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type QuickChargerRemainingTimeListProps = {
  data: QuickChargerRemainingTime[];
  updatedAt: Date | null;
};

export const QuickChargerRemainingTimeList = (
  props: QuickChargerRemainingTimeListProps
) => {
  const { data, updatedAt } = props;

  const renderDiffTime = (diff: number) => {
    if (diff < 0) return "0分";

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours === 0 ? `${mins}分` : `${hours}時間${mins}分`;
  };

  const calculateElapsedTime = (startAt?: string) => {
    if (!startAt || !updatedAt) return QuickChargerRemainingTimeConstants.EmptyValue;

    const start = new Date(startAt).getTime();
    const diff = updatedAt.getTime() - start;

    return renderDiffTime(diff);
  };

  const calculateRemainingTime = (estimatedEndAt?: string) => {
    if (!estimatedEndAt || !updatedAt) return QuickChargerRemainingTimeConstants.EmptyValue;

    const end = new Date(estimatedEndAt).getTime();
    const diff = end - updatedAt.getTime();

    const txt = renderDiffTime(diff);

    return txt.includes("分") ? `残り${txt}` : txt;
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead className="list-head">
          <TableRow>
            <TableCell>{QuickChargerRemainingTimeConstants.OfficeName}</TableCell>
            <TableCell>{QuickChargerRemainingTimeConstants.ChargerType}</TableCell>
            <TableCell>{QuickChargerRemainingTimeConstants.ChargerName}</TableCell>
            <TableCell>{QuickChargerRemainingTimeConstants.ConnectorNo}</TableCell>
            <TableCell>{QuickChargerRemainingTimeConstants.ChargingStatus}</TableCell>
            <TableCell>{QuickChargerRemainingTimeConstants.ChargingStartDatetime}</TableCell>
            <TableCell>{QuickChargerRemainingTimeConstants.ChargingElapsedTime}</TableCell>
            <TableCell>
              <div style={{ display: "flex", alignItems: "center" }}>
                {QuickChargerRemainingTimeConstants.RemainingMinutesToSoc90}
                <Tooltip
                  title={
                    <div style={{ whiteSpace: "pre-line" }}>
                      {QuickChargerRemainingTimeConstants.RemainingMinutesAttention}
                    </div>
                  }
                  placement="top"
                  arrow
                >
                  <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5 }} />
                </Tooltip>
              </div>
            </TableCell>
            <TableCell>{QuickChargerRemainingTimeConstants.Soc}</TableCell>
            <TableCell>{QuickChargerRemainingTimeConstants.ChargedEnergy}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.officeName}</TableCell>
              <TableCell>{ChargerDivision[row.chargerType]}</TableCell>
              <TableCell>{row.chargerName}</TableCell>
              <TableCell>{row.connectorNo ?? QuickChargerRemainingTimeConstants.UnsupportedValue}</TableCell>
              <TableCell>{row.isCharging ? QuickChargerRemainingTimeConstants.IsCharging : QuickChargerRemainingTimeConstants.IsNotCharging}</TableCell>
              <TableCell>{getShortDatetime(row.chargingAt)}</TableCell>
              <TableCell>{calculateElapsedTime(row.chargingAt)}</TableCell>
              {
                ChargerDivision[row.chargerType] === "急速" ? (
                  <>
                    <TableCell>{calculateRemainingTime(row.estimatedSoc90PercentAt)}</TableCell>
                    <TableCell>{row.evCarSocPercent != null ? `${row.evCarSocPercent}%` : QuickChargerRemainingTimeConstants.EmptyValue}</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{QuickChargerRemainingTimeConstants.UnsupportedValue}</TableCell>
                    <TableCell>{QuickChargerRemainingTimeConstants.UnsupportedValue}</TableCell>
                  </>
                )
              }
              <TableCell>{row.chargedKwh != null ? formatKwh(row.chargedKwh) : QuickChargerRemainingTimeConstants.EmptyValue}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
